import { useBodyMeasurementEntries } from "@/hooks/useBodyMeasurements";
import { isoWeekOf } from "@/lib/days";
import type {
  DayWidgetWeekEntry,
  MealLogConfig,
  MealLogSummaryConfig,
  WidgetWeekEntry,
} from "@/lib/schema";
import { conditionLabel, formatAmount } from "@/widgets/meal-log/metrics";
import type { WidgetWeekViewProps } from "@/widgets/types";
import {
  type ConditionRow,
  conditionRows,
  countedDays,
  countedDayTotals,
  energySummary,
  formatKcal,
  formatSignedKcal,
  formatSignedKilograms,
  KCAL_PER_KILOGRAM,
  measuredWeightChange,
  reconciledWeek,
  weightComparisonRange,
} from "./summary";

type Props = WidgetWeekViewProps<MealLogSummaryConfig>;

/**
 * The referenced meal-log instance, or null. A disabled instance counts as
 * absent — matching weekly-point-summary, which contributes 0 for a disabled
 * reference rather than reading through it.
 */
function findMealLog(
  entries: WidgetWeekEntry[],
  mealLogId: string,
): DayWidgetWeekEntry | null {
  const entry = entries.find((e) => e.config.id === mealLogId);
  if (!entry) return null;
  if (entry.config.kind !== "meal-log") return null;
  if (!entry.config.enabled) return null;
  if (!("days" in entry)) return null;
  return entry as DayWidgetWeekEntry;
}

export function WeekView({ config, allWidgets, previousWeek, week }: Props) {
  // Anchored to the viewed week; the settings preview (no week) falls back to
  // the current ISO week, exactly as body-measurement-trend does.
  const anchor = week
    ? { year: week.year, weekNumber: week.weekNumber }
    : isoWeekOf(new Date());
  // Measured change is only ever reported for the last COMPLETE week, so the
  // window ends there — the viewed week's own weigh-ins are never read
  // (docs/adr/0013).
  const reconciled = reconciledWeek(anchor.year, anchor.weekNumber);
  const range = weightComparisonRange(reconciled.year, reconciled.weekNumber);
  const { entries: measurements } = useBodyMeasurementEntries(
    range.startDate,
    range.endDate,
  );

  const mealLog = findMealLog(allWidgets, config.mealLogId);
  const mealLogConfig = mealLog?.config as MealLogConfig | undefined;
  const counted = mealLog ? countedDays(mealLog.days) : [];
  const totals = mealLog ? countedDayTotals(mealLog.days, counted) : [];
  const rows = mealLogConfig ? conditionRows(mealLogConfig, totals) : [];
  const energy = energySummary(totals, config.dailyEnergyExpenditure);

  // The reconciliation reads last week's food out of last week's sealed
  // snapshot, but scores it with THIS card's Energiforbruk — the figure being
  // calibrated. Turning the knob moves Forventet toward Faktisk, which is the
  // whole point; last week's own card reconciles with its own sealed figure.
  const previousMealLog = previousWeek
    ? findMealLog(previousWeek.widgets, config.mealLogId)
    : null;
  const previousCounted = previousMealLog
    ? countedDays(previousMealLog.days)
    : [];
  const previousEnergy = energySummary(
    previousMealLog
      ? countedDayTotals(previousMealLog.days, previousCounted)
      : [],
    config.dailyEnergyExpenditure,
  );
  const previousActual = measuredWeightChange(
    measurements,
    reconciled.year,
    reconciled.weekNumber,
  );

  const showEnergyBlock =
    typeof config.dailyEnergyExpenditure === "number" &&
    Number.isFinite(config.dailyEnergyExpenditure);

  return (
    <section className="widget-card" data-testid={`widget-week-${config.id}`}>
      <header className="widget-card-head">
        <h3 className="widget-card-title">{config.label}</h3>
        {config.mealLogId ? (
          <div className="widget-card-head-right">
            <span
              className="widget-card-score"
              data-testid={`meal-log-summary-counted-days-${config.id}`}
            >
              {counted.length} av 7 dager talt
            </span>
          </div>
        ) : null}
      </header>
      <div className="widget-card-body">
        {!config.mealLogId ? (
          <p
            className="hint"
            data-testid={`meal-log-summary-no-source-${config.id}`}
          >
            Ingen matdagbok valgt.
          </p>
        ) : (
          <>
            {counted.length === 0 ? (
              <p
                className="hint"
                data-testid={`meal-log-summary-empty-${config.id}`}
              >
                Ingen dager er ferdiglogget med måltider ennå.
              </p>
            ) : null}
            <ConditionTable configId={config.id} rows={rows} />
            {showEnergyBlock ? (
              <EnergyBlock
                configId={config.id}
                expenditure={config.dailyEnergyExpenditure as number}
                energy={energy}
                previousWeekNumber={reconciled.weekNumber}
                previousCountedDays={previousCounted.length}
                previousExpected={previousEnergy?.expectedChangeWeek ?? null}
                previousActual={previousActual}
              />
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}

function ConditionTable({
  configId,
  rows,
}: {
  configId: string;
  rows: ConditionRow[];
}) {
  if (rows.length === 0) return null;
  return (
    <div className="week-matrix meal-summary-table">
      <table data-testid={`meal-log-summary-table-${configId}`}>
        <thead>
          <tr>
            <th className="th-group">Betingelse</th>
            <th>Snitt</th>
            <th>Min</th>
            <th>Maks</th>
            <th>Oppfylt</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            // kcal is whole numbers; the gram metrics keep meal-log's one
            // decimal, so a fibre average never reads as a round number it is
            // not.
            const format = (value: number) =>
              row.metric === "kcal" ? formatKcal(value) : formatAmount(value);
            return (
              <tr
                key={row.key}
                data-testid={`meal-log-summary-row-${configId}-${row.key}`}
              >
                <td className="td-label">
                  {row.condition ? conditionLabel(row.condition) : "kcal"}
                </td>
                <td
                  data-testid={`meal-log-summary-average-${configId}-${row.key}`}
                >
                  {row.spread ? format(row.spread.average) : "—"}
                </td>
                <td
                  data-testid={`meal-log-summary-minimum-${configId}-${row.key}`}
                >
                  {row.spread ? format(row.spread.minimum) : "—"}
                </td>
                <td
                  data-testid={`meal-log-summary-maximum-${configId}-${row.key}`}
                >
                  {row.spread ? format(row.spread.maximum) : "—"}
                </td>
                <td data-testid={`meal-log-summary-met-${configId}-${row.key}`}>
                  {row.met === null ? "—" : `${row.met}/${row.total}`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function EnergyBlock({
  configId,
  expenditure,
  energy,
  previousWeekNumber,
  previousCountedDays,
  previousExpected,
  previousActual,
}: {
  configId: string;
  expenditure: number;
  energy: ReturnType<typeof energySummary>;
  previousWeekNumber: number;
  previousCountedDays: number;
  previousExpected: number | null;
  previousActual: number | null;
}) {
  const kilograms = (value: number | null) =>
    value === null ? "—" : `${formatSignedKilograms(value)} kg`;
  return (
    <div
      className="meal-summary-energy"
      data-testid={`meal-log-summary-energy-${configId}`}
    >
      <EnergyRow
        label="Energiforbruk"
        testid={`meal-log-summary-expenditure-${configId}`}
        value={`${formatKcal(expenditure)} kcal/dag`}
      />

      <p className="meal-summary-energy-heading">Denne uken</p>
      <EnergyRow
        label="Energibalanse"
        testid={`meal-log-summary-balance-${configId}`}
        value={
          energy ? `${formatSignedKcal(energy.energyBalance)} kcal/dag` : "—"
        }
      />
      <EnergyRow
        label="Forventet, hittil"
        testid={`meal-log-summary-expected-so-far-${configId}`}
        value={kilograms(energy?.expectedChangeSoFar ?? null)}
      />
      <EnergyRow
        label="Forventet, hele uken"
        testid={`meal-log-summary-expected-week-${configId}`}
        value={kilograms(energy?.expectedChangeWeek ?? null)}
      />

      {/* Measured change belongs to the last COMPLETE week and nowhere near
          this week's figures: on a Monday the two would span different periods
          and read as cause and effect anyway (docs/adr/0013). */}
      <p className="meal-summary-energy-heading">
        Uke {previousWeekNumber}
        <span
          className="meal-summary-energy-note"
          data-testid={`meal-log-summary-previous-counted-days-${configId}`}
        >
          {previousCountedDays} av 7 dager talt
        </span>
      </p>
      <EnergyRow
        label="Forventet"
        testid={`meal-log-summary-previous-expected-${configId}`}
        value={kilograms(previousExpected)}
      />
      <EnergyRow
        label="Faktisk"
        testid={`meal-log-summary-previous-actual-${configId}`}
        value={kilograms(previousActual)}
      />
      <p className="hint">
        {energy
          ? `Snitt ${formatKcal(energy.averageKcal)} kcal mot ${formatKcal(expenditure)} kcal forbruk · ${KCAL_PER_KILOGRAM} kcal per kg kroppsfett.`
          : `${KCAL_PER_KILOGRAM} kcal per kg kroppsfett.`}{" "}
        Faktisk er ukesnitt mot uken før — bare hele uker kan sammenlignes.
      </p>
    </div>
  );
}

function EnergyRow({
  label,
  testid,
  value,
}: {
  label: string;
  testid: string;
  value: string;
}) {
  return (
    <div className="meal-summary-energy-row">
      <span className="meal-summary-energy-label">{label}</span>
      <span className="meal-summary-energy-value" data-testid={testid}>
        {value}
      </span>
    </div>
  );
}
